import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrencyDollars } from '../../../utils/common';
import { useAuthContext } from '../../../features/auth/context/AuthContext';
import Lottie from 'lottie-react';
import nvgtrLoader from '../../../loaders/nvgtr_loader_white.json';
import nvgtrLoaderWhite from '../../../loaders/nvgtr_loader_white.json';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import 'react-loading-skeleton/dist/skeleton.css';

const BillingSection = ({ embedded = false }) => {
  const { user } = useAuthContext();
  const { darkMode } = useTheme();
  const [billingData, setBillingData] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  
  const [isOrgOwner, setIsOrgOwner] = useState(false);
  const [isOrgAdminOrOwner, setIsOrgAdminOrOwner] = useState(false);

  // New state for invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Helper function to check if user is organization owner
  const checkIfUserIsOwner = (user) => {
    if (!user) return false;
    
    // Check multiple possible role properties
    const organizationRole = user?.organizationRole;
    const companyRole = user?.company?.role;
    const userRole = user?.role;
    const isCompanyOwner = user?.isCompanyOwner;
    const isOwner = user?.isOwner;
    
    // Check various ways the owner role might be stored
    return (
      organizationRole === 'owner' ||
      companyRole === 'owner' ||
      userRole === 'owner' ||
      isCompanyOwner === true ||
      isOwner === true ||
      // Sometimes it might be stored as 'admin' but with additional owner flags
      (organizationRole === 'admin' && isCompanyOwner === true) ||
      (companyRole === 'admin' && isOwner === true)
    );
  };

  const scrollbarStyle = {
    maxHeight: 'calc(100vh - 150px)',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    msOverflowStyle: 'none',  /* IE and Edge */
    scrollbarColor: '#444444 transparent'
  };

  // Simplified scrollbar styling without hover states
  const scrollbarStylesWebkit = `
    .billing-section::-webkit-scrollbar {
      width: 8px;
    }
    .billing-section::-webkit-scrollbar-track {
      background: transparent;
    }
    .billing-section::-webkit-scrollbar-thumb {
      background-color: #444444;
      border-radius: 20px;
    }
  `;

  // Modernized layout for embedded (AccountModal) vs standalone (Profile)
  const sectionCardCls = embedded
    ? 'py-5 border-b border-gray-100 dark:border-[#2a2a2a] first:pt-0'
    : 'rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-[#2a2a2a]';
  const sectionTitleCls = embedded
    ? 'text-sm font-medium text-gray-500 dark:text-gray-400 mb-3'
    : 'text-xl font-semibold mb-4 text-gray-900 dark:text-white';
  const sectionTitleLgCls = embedded
    ? 'text-sm font-medium text-gray-500 dark:text-gray-400 mb-3'
    : 'text-xl font-semibold mb-6 text-gray-900 dark:text-white';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const usePlaceholders = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BILLING_PLACEHOLDERS === 'true';
        if (usePlaceholders) {
          const now = new Date();
          const nextMonth = new Date(now);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          const periodStart = Math.floor(new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000);
          const periodEnd = Math.floor(new Date(now.getFullYear(), now.getMonth() + 1, 0).getTime() / 1000);

          setBillingData({
            subscriptionStatus: 'active',
            subscriptionPlan: 'pro',
            hasPaymentMethod: true,
            stripeConfigured: true,
            billingCycle: 'monthly',
            nextBillingDate: nextMonth.toISOString(),
            activeMembers: 5,
            billableMemberCount: 5,
            upcomingInvoice: {
              next_payment_attempt: nextMonth.getTime() / 1000,
              period_start: periodStart,
              period_end: periodEnd,
              total: 11150,
              hasProrations: true,
              regularCharges: [
                { id: 'ch_1', description: 'Pro Plan', quantity: 1, unitAmount: 9900, amount: 9900 },
              ],
              currentPeriodCharges: [
                { id: 'ch_2', description: 'Pro Team Member', isProration: true, amount: 1250, quantity: 1, unitAmount: 2500, period: { start: periodStart, end: periodEnd }, memberInfo: { joinedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(), fullName: 'Jane Smith', email: 'jane@example.com' } },
              ],
            },
          });
          setPaymentMethods([{ brand: 'visa', last4: '4242', exp_month: 12, exp_year: 2026 }]);
          setUsageData({ creditsUsed: 342 });
          setIsOrgOwner(true);
          setIsOrgAdminOrOwner(true);
          setInvoices([
            { id: 'in_1', number: 'INV-001', status: 'paid', amount_paid: 9900, amount_due: 0, currency: 'usd', created: Math.floor(now.getTime() / 1000) - 30 * 24 * 3600, period_start: periodStart - 30 * 24 * 3600, period_end: periodEnd - 30 * 24 * 3600, lines: [{ period: { start: periodStart - 30 * 24 * 3600, end: periodEnd - 30 * 24 * 3600 } }] },
            { id: 'in_2', number: 'INV-002', status: 'paid', amount_paid: 9900, amount_due: 0, currency: 'usd', created: Math.floor(now.getTime() / 1000) - 60 * 24 * 3600, period_start: periodStart - 60 * 24 * 3600, period_end: periodEnd - 60 * 24 * 3600, lines: [{ period: { start: periodStart - 60 * 24 * 3600, end: periodEnd - 60 * 24 * 3600 } }] },
          ]);
          setInvoicesLoading(false);
          setLoading(false);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🚀 BillingSection fetchData started, user:', user?.email);
        }
        const token = localStorage.getItem('token');
        
        console.log('📡 Fetching organization billing data...');
        
        // Try organization billing first, fallback to company billing if needed
        let billingResponse;
        try {
          billingResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/team-billing/billing-details`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Organization billing response:', billingResponse.data);
          }
        } catch (orgBillingError) {
          // If organization billing fails and user has company subscription, try company billing
          if (user?.companyId && user?.company?.subscriptionPlan) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ Organization billing failed, trying company billing fallback...');
              console.log('📡 Fetching company billing data...');
            }
            
            try {
              billingResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/billing/billing-details`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ Company billing response:', billingResponse.data);
              }
            } catch (companyBillingError) {
              console.error('❌ Company billing also failed:', companyBillingError);
              throw orgBillingError; // Throw original organization error
            }
          } else {
            throw orgBillingError; // Re-throw if no company fallback available
          }
        }
        
        setBillingData(billingResponse.data);

        // Fetch usage data for usage-based billing
        try {
          const usageResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/team-billing/usage-details`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (process.env.NODE_ENV === 'development') {
            console.log('📊 Usage response:', usageResponse.data);
          }
          setUsageData(usageResponse.data);
          
          // Set usage-based pricing state from response
          // Check if user is organization owner by fetching organization data
          try {
            const orgResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/organization`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const isOwner = orgResponse.data?.organization?.userRole === 'owner';
            const isAdminOrOwner = orgResponse.data?.organization?.userRole === 'owner' || 
                                  orgResponse.data?.organization?.userRole === 'admin';
            
            setIsOrgOwner(isOwner);
            setIsOrgAdminOrOwner(isAdminOrOwner);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('🔍 Organization role check:', {
                userEmail: user?.email,
                organizationData: orgResponse.data?.organization,
                userRole: orgResponse.data?.organization?.userRole,
                isOwner: isOwner,
                isAdminOrOwner: isAdminOrOwner
              });
            }
            
            
          } catch (orgError) {
            console.warn('⚠️ Could not fetch organization data for role check:', orgError);
            // Fallback to user object role check
            const fallbackIsOwner = checkIfUserIsOwner(user);
            setIsOrgOwner(fallbackIsOwner);
            setIsOrgAdminOrOwner(fallbackIsOwner); // Conservative fallback
          }
        } catch (usageError) {
          console.warn('⚠️ Could not fetch usage data:', usageError);
          setUsageData(null);
        }
        
        // Fetch invoices
        await fetchInvoices();
        
        // Store debug info for development
        if (process.env.NODE_ENV === 'development') {
          const debugData = {
            userEmail: user?.email,
            subscriptionStatus: billingResponse.data?.subscriptionStatus,
            hasPaymentMethod: billingResponse.data?.hasPaymentMethod,
            billingCycle: billingResponse.data?.billingCycle,
            nextBillingDate: billingResponse.data?.nextBillingDate,
            activeMembers: billingResponse.data?.activeMembers,
            billableMemberCount: billingResponse.data?.billableMemberCount,
            subscriptionPlan: billingResponse.data?.subscriptionPlan,
            stripeConfigured: billingResponse.data?.stripeConfigured,
            dataSource: billingResponse.data?.stripeConfigured ? 'Stripe API (live data)' : 'Database fallback',
            usageData: usageData
          };
          console.log('🔍 Debug info:', debugData);
          setDebugInfo(debugData);
        }
        
        // Fetch payment methods
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('💳 Attempting to fetch organization payment methods...');
          }
          const paymentResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/team-billing/payment-methods`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (process.env.NODE_ENV === 'development') {
            console.log('💳 Organization payment methods response:', paymentResponse.data);
          }
          
          setPaymentMethods(paymentResponse.data.paymentMethods || []);
        } catch (paymentError) {
          console.warn('⚠️ Could not fetch organization payment methods:', paymentError);
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Payment error details:', {
              status: paymentError.response?.status,
              message: paymentError.response?.data?.message,
              error: paymentError.response?.data?.error
            });
          }
          
          // Set empty array if organization payment methods can't be fetched
          setPaymentMethods([]);
        }
        
      } catch (err) {
        console.error('❌ Error fetching billing data:', err);
        console.error('❌ Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError(err.message || 'Failed to load billing information');
      } finally {
        setLoading(false);
      }
    };

    const usePlaceholders = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BILLING_PLACEHOLDERS === 'true';
    if (user?.email || usePlaceholders) {
      fetchData();
    } else {
      console.log('⏳ Waiting for user to be loaded...');
    }
  }, [user?.email]);

  const handleBillingPortalRedirect = async () => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BILLING_PLACEHOLDERS === 'true') {
      setPortalLoading(true);
      await new Promise((r) => setTimeout(r, 800));
      setPortalLoading(false);
      return;
    }
    setPortalLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Pre-flight check: Validate user and billing state
      if (!user?.email) {
        throw new Error('User email not available. Please refresh the page and try again.');
      }
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Debug billing state before making the request
      console.log('🔍 Pre-flight billing state check:', {
        userEmail: user.email,
        companyId: user?.companyId,
        organizationId: user?.organizationId,
        companySubscriptionPlan: user?.company?.subscriptionPlan,
        billingDataStatus: billingData?.subscriptionStatus,
        billingDataPlan: billingData?.subscriptionPlan,
        hasPaymentMethod: billingData?.hasPaymentMethod,
        stripeConfigured: billingData?.stripeConfigured,
        activeMembers: billingData?.activeMembers,
        billableMemberCount: billingData?.billableMemberCount
      });
      
      // Debug: Log the request we're about to make
      const requestPayload = { 
        returnUrl: `${window.location.origin}/?return_from_portal=true`,
        // Optional: Add configuration_id if you have multiple portal configurations  
        // configurationId: 'bpc_...' 
      };
      
      console.log('🔄 Creating organization billing portal session with payload:', {
        url: `${process.env.REACT_APP_API_URL}/team-billing/create-billing-portal-session`,
        payload: requestPayload,
        userEmail: user?.email,
        hasToken: !!token,
        tokenPrefix: token ? token.substring(0, 20) + '...' : 'NO TOKEN'
      });
      
      // Try organization billing first
      let response;
      try {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/team-billing/create-billing-portal-session`,
          requestPayload,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            } 
          }
        );
        console.log('✅ Organization billing portal session created:', response.data);
      } catch (orgError) {
        // If organization billing fails with "No billing setup", try company billing fallback
        if (orgError.response?.status === 400 && 
            orgError.response?.data?.error === 'BILLING_NOT_SETUP' &&
            user?.companyId && user?.company?.subscriptionPlan) {
          
          console.log('⚠️ Organization billing not setup, trying company billing fallback...');
          console.log('🔄 Creating company billing portal session...');
          
          try {
            response = await axios.post(
              `${process.env.REACT_APP_API_URL}/billing/create-billing-portal-session`,
              requestPayload,
              { 
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                } 
              }
            );
            console.log('✅ Company billing portal session created:', response.data);
          } catch (companyError) {
            console.error('❌ Company billing also failed:', companyError);
            throw orgError; // Throw original organization error
          }
        } else {
          throw orgError; // Re-throw if not a billing setup issue
        }
      }
      
      // Validate the response contains the required URL
      if (!response?.data?.url) {
        throw new Error('Invalid response from billing portal session creation');
      }
      
      // Redirect to Stripe Customer Portal
      // Using window.location.href as recommended by Stripe docs
      window.location.href = response.data.url;
      
    } catch (err) {
      console.error('❌ Error creating billing portal session:', err);
      
      // Enhanced error logging for debugging
      console.error('❌ Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        requestUrl: err.config?.url,
        requestData: err.config?.data,
        userInfo: {
          email: user?.email,
          companyId: user?.companyId,
          subscriptionPlan: user?.company?.subscriptionPlan,
          billingStatus: billingData?.subscriptionStatus
        }
      });
      
      // Extract meaningful error message
      let errorMessage = 'Failed to open billing portal';
      
      if (err.response?.status === 400) {
        if (err.response.data?.error) {
          errorMessage = `${err.response.data.error}`;
        } else if (err.response.data?.message) {
          errorMessage = `${err.response.data.message}`;
        } else {
          errorMessage = 'Invalid request data. Please check your subscription status and try again.';
        }
        
        // Special handling for "No billing setup" error
        if (err.response.data?.message?.includes('No billing setup')) {
          errorMessage = 'Organization billing is not properly set up. Please contact support to configure billing for your organization.';
        }
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission to access billing portal.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Billing portal endpoint not found. Please contact support.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setPortalLoading(false);
    }
  };

  // Handle return from Stripe Customer Portal (URL param or localStorage flag from MainInterface)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnFromUrl = urlParams.get('return_from_portal') === 'true';
    const returnFromStorage = localStorage.getItem('billingReturnFromPortal') === '1';
    const returnFromPortal = returnFromUrl || returnFromStorage;

    if (returnFromPortal) {
      // Clean URL if present (Profile/billing is retired - we now return to /)
      if (returnFromUrl) {
        const newUrl = window.location.origin + (window.location.pathname || '/');
        window.history.replaceState({}, document.title, newUrl);
      }
      localStorage.removeItem('billingReturnFromPortal');

      // Refresh billing data after returning from portal
      if (user?.email) {
        const fetchData = async () => {
          setLoading(true);
          try {
            const token = localStorage.getItem('token');
            
            const billingResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/team-billing/billing-details`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setBillingData(billingResponse.data);
            
            // Refresh payment methods
            try {
              const paymentResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/team-billing/payment-methods`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setPaymentMethods(paymentResponse.data.paymentMethods || []);
            } catch (paymentError) {
              console.warn('⚠️ Could not fetch payment methods:', paymentError);
              setPaymentMethods([]);
            }
            
          } catch (err) {
            console.error('❌ Error refreshing billing data:', err);
            setError(err.message || 'Failed to refresh billing information');
          } finally {
            setLoading(false);
          }
        };
        
        fetchData();
      }
    }
  }, [user?.email]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getCardBrandIcon = (brand) => {
    const iconClass = 'w-8 h-8 flex-shrink-0';
    const defaultIconClass = 'w-8 h-8 flex-shrink-0 text-gray-700 dark:text-gray-300';
    const brandMap = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'AmericanExpress',
      american_express: 'AmericanExpress',
      discover: 'Discover',
      diners: 'DinersClub',
      diners_club: 'DinersClub',
      jcb: 'Jcb',
    };
    const type = brandMap[brand?.toLowerCase()];
    if (type) {
      return <PaymentIcon type={type} format="flatRounded" width={32} height={32} className={iconClass} />;
    }
    return <CreditCardIcon className={defaultIconClass} />;
  };

  const getSubscriptionStatus = () => {
    if (!billingData) return 'Loading...';
    
    switch (billingData.subscriptionStatus) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';  
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'none':
      default:
        return 'No active subscription';
    }
  };

  const getSubscriptionName = () => {
    if (!billingData) return 'Loading...';
    
    // If there's a subscription plan, use that
    if (billingData.subscriptionPlan && billingData.subscriptionPlan !== 'none') {
      const planName = billingData.subscriptionPlan.toLowerCase();
      switch (planName) {
        case 'solo':
          return 'Solo Plan';
        case 'pro':
          return 'Pro Plan';
        case 'max':
          return 'Max Plan';
        case 'unknown':
          return 'Active Subscription';
        default:
          // Capitalize first letter for any other plan names
          return `${planName.charAt(0).toUpperCase() + planName.slice(1)} Plan`;
      }
    }
    
    // Otherwise check subscription status
    if (billingData.subscriptionStatus && billingData.subscriptionStatus !== 'none') {
      return 'Active Plan';
    }
    
    return 'No active plan';
  };

  const getSubscriptionTerm = () => {
    if (!billingData) return '';
    return billingData.billingCycle === 'annual' ? 'Annual' : 'Monthly';
  };

  // Manage Billing button - modernized to match SettingsTab/SecurityTab
  const ManageBillingButton = ({ variant = 'default', text = 'Manage Billing' }) => (
    <button 
      onClick={handleBillingPortalRedirect}
      disabled={portalLoading}
      className={`account-modal-btn px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
        variant === 'primary' 
          ? 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50' 
          : variant === 'danger'
          ? 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400'
          : 'border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#252525] disabled:opacity-50'
      } ${portalLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {text}
    </button>
  );

  // Handler for billing cycle changes
  const handleBillingCycleChange = async (billingOption) => {
    setPortalLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/change-billing-cycle`,
        { billingOption },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Refresh billing data to show updated information
        window.location.reload();
      }
    } catch (err) {
      console.error('Error changing billing cycle:', err);
      setError(err.response?.data?.message || 'Failed to change billing cycle');
    } finally {
      setPortalLoading(false);
    }
  };

  // Function to fetch invoices
  const fetchInvoices = async () => {
    if (invoicesLoading) return; // Prevent multiple simultaneous calls
    
    setInvoicesLoading(true);
    setInvoicesError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/invoices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setInvoices(response.data.invoices);
        console.log(`✅ Fetched ${response.data.invoices.length} invoices`);
      } else {
        throw new Error('Failed to fetch invoices');
      }
    } catch (err) {
      console.error('❌ Error fetching invoices:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load invoices';
      setInvoicesError(errorMessage);
      
      // Don't show error for billing not setup - this is expected for new accounts
      if (err.response?.data?.error !== 'BILLING_NOT_SETUP') {
        setError(errorMessage);
      }
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Function to download invoice
  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoice(invoiceId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/invoices/${invoiceId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.download_url) {
        // Create a temporary link element and trigger download
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.download = response.data.filename || `invoice-${invoiceNumber || invoiceId}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log(`✅ Downloaded invoice ${invoiceNumber || invoiceId}`);
      } else {
        throw new Error('Download URL not available');
      }
    } catch (err) {
      console.error('❌ Error downloading invoice:', err);
      const errorMessage = err.response?.data?.message || 'Failed to download invoice';
      setError(errorMessage);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Function to format currency
  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Stripe amounts are in cents
  };

  // Function to format invoice status
  const getInvoiceStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium';
    
    switch (status) {
      case 'paid':
        return <span className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400`}>Paid</span>;
      case 'open':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400`}>Open</span>;
      case 'draft':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400`}>Draft</span>;
      case 'void':
        return <span className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400`}>Void</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400`}>{status}</span>;
    }
  };

  // Helper function to format prorated charge descriptions to be more user-friendly
  const formatProratedDescription = (charge) => {
    if (!charge.isProration || !charge.period) {
      return charge.description;
    }

    // Extract product name from description
    // Handle patterns like "Remaining time on Pro Team Member after 26 Jun 2025"
    let productName = charge.description;
    
    // Remove "Remaining time on " prefix if present
    if (productName.includes('Remaining time on ')) {
      productName = productName.replace('Remaining time on ', '');
    }
    
    // Remove " after [date]" suffix if present
    const afterPattern = / after \d{1,2} \w{3} \d{4}/;
    if (afterPattern.test(productName)) {
      productName = productName.replace(afterPattern, '');
    }

    // Return just the cleaned product name (no "Prorated" prefix)
    return productName;
  };

  // Helper function to get member info for prorated charges
  const getMemberInfoForProratedCharge = (charge) => {
    // Use member info from backend if available
    if (charge.memberInfo) {
      let daysRemaining = 0;
      
      // Calculate days remaining from join date to next billing date
      try {
        const joinDate = charge.memberInfo.joinedAt ? new Date(charge.memberInfo.joinedAt) : new Date(charge.period.start * 1000);
        
        // Get the next billing date from billingData
        const nextBillingDate = billingData?.nextBillingDate ? new Date(billingData.nextBillingDate) : new Date(charge.period.end * 1000);
        
        // Calculate days from join date to next billing date
        const timeDiff = nextBillingDate.getTime() - joinDate.getTime();
        daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
        
        // Remove the spamming console.log
        // console.log('📅 Days calculation:', {
        //   joinDate: joinDate.toDateString(),
        //   nextBillingDate: nextBillingDate.toDateString(),
        //   daysRemaining
        // });
        
      } catch (error) {
        console.warn('Error calculating days remaining:', error);
        daysRemaining = 0;
      }
      
      return {
        joinDate: charge.memberInfo.joinedAt ? new Date(charge.memberInfo.joinedAt) : new Date(charge.period.start * 1000),
        daysDiff: daysRemaining,
        memberName: charge.memberInfo.fullName,
        memberEmail: charge.memberInfo.email
      };
    }
    
    // Fallback to extracting what we can from the charge period
    if (!charge.period) return null;

    let daysRemaining = 0;
    
    try {
      const joinDate = new Date(charge.period.start * 1000);
      const nextBillingDate = billingData?.nextBillingDate ? new Date(billingData.nextBillingDate) : new Date(charge.period.end * 1000);
      
      const timeDiff = nextBillingDate.getTime() - joinDate.getTime();
      daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
    } catch (error) {
      console.warn('Error calculating fallback days:', error);
      daysRemaining = 0;
    }

    return {
      joinDate: new Date(charge.period.start * 1000),
      daysDiff: daysRemaining,
      // Fallback values when backend doesn't provide member info
      memberName: 'Team Member',
      memberEmail: null
    };
  };

  // Helper function to get the actual billing period from invoice line items
  // Stripe's top-level invoice period_start/period_end represent "billable accrual period" 
  // but line items contain the actual service period the customer is paying for
  const getActualBillingPeriod = (invoice) => {
    if (!invoice.lines || invoice.lines.length === 0) {
      // Fallback to invoice period if no line items
      return {
        start: invoice.period_start,
        end: invoice.period_end
      };
    }

    // Find subscription line items (these contain the service periods)
    const subscriptionLines = invoice.lines.filter(line => 
      line.period && line.period.start && line.period.end
    );

    if (subscriptionLines.length === 0) {
      // Fallback to invoice period if no subscription lines with periods
      return {
        start: invoice.period_start,
        end: invoice.period_end
      };
    }

    // For multiple subscription lines, find the earliest start and latest end
    const periods = subscriptionLines.map(line => line.period);
    const earliestStart = Math.min(...periods.map(p => p.start));
    const latestEnd = Math.max(...periods.map(p => p.end));

    return {
      start: earliestStart,
      end: latestEnd
    };
  };

  if (loading) {
    const wrapperClass = embedded ? 'space-y-6' : 'space-y-8 pr-4 billing-section';
    const wrapperStyle = embedded ? undefined : scrollbarStyle;
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {!embedded && <style>{scrollbarStylesWebkit}</style>}
        <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
          {/* Current Period Charges skeleton - matches actual layout */}
          <div className={sectionCardCls}>
            <div className="flex items-center justify-between mb-6">
              <Skeleton height={embedded ? 16 : 24} width={200} />
              <div className="flex items-center gap-4">
                <Skeleton height={14} width={120} />
                <Skeleton height={36} width={80} />
              </div>
            </div>
            <div className="mb-6">
              <Skeleton height={14} width={140} className="mb-3" />
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2">
                  <Skeleton height={16} width={180} />
                  <Skeleton height={16} width={60} />
                </div>
                <div className="flex justify-between items-center py-2">
                  <Skeleton height={16} width={140} />
                  <Skeleton height={16} width={50} />
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-[#2a2a2a]">
              <div>
                <Skeleton height={16} width={160} />
                <Skeleton height={12} width={140} className="mt-1" />
              </div>
              <Skeleton height={20} width={70} />
            </div>
          </div>

          {/* Invoices skeleton - matches actual layout */}
          <div className={sectionCardCls}>
            <div className="flex items-center justify-between mb-6">
              <Skeleton height={embedded ? 16 : 24} width={100} />
            </div>
            <div className="space-y-0">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`flex items-center justify-between py-3 ${i < 2 ? 'border-b border-gray-200 dark:border-[#2a2a2a]' : ''}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Skeleton height={14} width={90} />
                      <Skeleton height={18} width={50} borderRadius={4} />
                    </div>
                    <Skeleton height={12} width={140} />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Skeleton height={14} width={50} className="mb-1" />
                    </div>
                    <Skeleton height={32} width={90} borderRadius={8} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method skeleton - matches actual layout */}
          <div className={sectionCardCls}>
            <div className="flex items-center justify-between mb-4">
              <Skeleton height={embedded ? 16 : 20} width={140} />
              <Skeleton height={36} width={80} borderRadius={8} />
            </div>
            <div className="flex items-center space-x-3">
              <Skeleton height={32} width={42} borderRadius={4} />
              <div className="space-y-1">
                <Skeleton height={16} width={140} />
                <Skeleton height={12} width={80} />
              </div>
            </div>
          </div>

          {/* Usage Controls skeleton - matches actual layout */}
          <div className={sectionCardCls}>
            <Skeleton height={embedded ? 16 : 20} width={120} className="mb-4" />
            <div className="account-modal-shape flex items-center justify-between p-4 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg">
              <div className="flex-1">
                <Skeleton height={16} width={140} className="mb-2" />
                <Skeleton height={14} width="80%" />
              </div>
              <Skeleton height={20} width={36} borderRadius={10} className="ml-4" />
            </div>
          </div>

          {/* Usage Statistics skeleton - matches actual layout */}
          <div className={sectionCardCls}>
            <Skeleton height={embedded ? 16 : 20} width={140} className="mb-4" />
            <div className="account-modal-shape p-4 bg-gray-50/50 dark:bg-[#181818] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
              <Skeleton height={14} width={160} className="mb-3" />
              <div className="space-y-2">
                <Skeleton height={12} width="100%" />
                <Skeleton height={12} width="90%" />
                <Skeleton height={8} width="100%" className="mt-3" />
                <Skeleton height={12} width={120} />
              </div>
            </div>
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-modal-shape p-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <h2 className="text-lg font-semibold mb-3 text-red-700 dark:text-red-400">Error Loading Billing Information</h2>
        <p className="text-red-600 dark:text-red-300 text-sm mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="account-modal-btn px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  // Check if organization has active subscription AND proper billing setup
  const hasActiveSubscription = (billingData?.subscriptionStatus === 'active' || 
                                billingData?.subscriptionStatus === 'trialing' ||
                                billingData?.subscriptionStatus === 'past_due') &&
                               (billingData?.subscriptionPlan && billingData.subscriptionPlan !== 'none') &&
                               billingData?.stripeConfigured; // Only show billing if Stripe is configured
  
  // Check if subscription has payment issues (past_due)
  const hasPaymentIssue = billingData?.subscriptionStatus === 'past_due' &&
                         (billingData?.subscriptionPlan && billingData.subscriptionPlan !== 'none') &&
                         billingData?.stripeConfigured;
  
  // Additional check: if we have a subscription plan but no proper billing setup, 
  // treat it as no subscription to prevent billing portal errors
  const hasBillingSetup = billingData?.hasPaymentMethod || 
                         (billingData?.subscriptionStatus === 'active' && billingData?.stripeConfigured);

  // We'll handle payment issues as a notification in the main billing section, not as a complete replacement

  if (!hasActiveSubscription || !hasBillingSetup) {
    const wrapperClass = embedded ? 'space-y-6' : 'space-y-8 pr-4 billing-section';
    const wrapperStyle = embedded ? undefined : scrollbarStyle;
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {!embedded && <style>{scrollbarStylesWebkit}</style>}
        <div className={sectionCardCls}>
          <h2 className={sectionTitleCls}>No Active Subscription</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {!billingData?.stripeConfigured 
              ? "Billing system is not properly configured. Please contact support."
              : "Your organization currently doesn't have any active subscriptions."
            }
          </p>
          {error && (
            <div className="account-modal-shape mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          {debugInfo && process.env.NODE_ENV === 'development' && (
            <div className="account-modal-shape mt-4 p-3 bg-[#ebebeb] dark:bg-[#282828] rounded-lg text-sm">
              <h4 className="font-semibold mb-2">Debug Info:</h4>
              <pre className="whitespace-pre-wrap">{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
          {billingData?.stripeConfigured && (
            <ManageBillingButton variant="primary" text="Set Up Subscription" />
          )}
        </div>
      </div>
    );
  }

  const wrapperClass = embedded ? 'space-y-6' : 'space-y-8 pr-4 billing-section';
  const wrapperStyle = embedded ? undefined : scrollbarStyle;
  return (
    <>
      {!embedded && <style>{scrollbarStylesWebkit}</style>}
      <div className={wrapperClass} style={wrapperStyle}>
        {process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BILLING_PLACEHOLDERS === 'true' && (
          <div className="account-modal-shape mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium">
            Dev: Showing placeholder billing data.
          </div>
        )}
        {/* Show portal loading state */}
        {portalLoading && (
          <div className="account-modal-shape p-4 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#1a1a1a]">
            <div className="flex items-center gap-3">
              <Lottie
                animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                loop={true}
                style={{ width: 24, height: 24 }}
              />
              <p className="text-gray-500 dark:text-gray-400">Opening customer portal...</p>
            </div>
          </div>
        )}

        {/* Show payment issue notification */}
        {hasPaymentIssue && (
          <div className="account-modal-shape p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">Payment Required</h3>
                <p className="text-red-600 dark:text-red-300 mb-3">
                  Your subscription payment is past due. Please update your payment method to continue using the platform without interruption.
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-red-600 dark:text-red-400">
                    <strong>Status:</strong> Past Due • <strong>Plan:</strong> {billingData.subscriptionPlan?.charAt(0).toUpperCase() + billingData.subscriptionPlan?.slice(1) || 'Unknown'}
                  </div>
                  <ManageBillingButton variant="danger" text="Update Payment Method" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show error state */}
        {error && (
          <div className="account-modal-shape p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <button 
              onClick={() => setError(null)} 
              className="account-modal-btn mt-2 text-sm text-red-600 dark:text-red-400 hover:underline px-2 py-1 rounded-lg"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Enhanced debug info for development */}
        {debugInfo && process.env.NODE_ENV === 'development' && (
          <details className="account-modal-shape-lg rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
            <summary className="text-sm font-semibold mb-2 text-blue-700 dark:text-blue-400 cursor-pointer hover:underline">
              Development Debug Info (Click to expand)
            </summary>
            <div className="mt-3 space-y-2">
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>User Email:</strong> {debugInfo.userEmail}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Subscription Status:</strong> {debugInfo.subscriptionStatus}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Subscription Plan:</strong> {debugInfo.subscriptionPlan}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Has Payment Method:</strong> {debugInfo.hasPaymentMethod ? 'Yes' : 'No'}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Stripe Configured:</strong> {debugInfo.stripeConfigured ? 'Yes' : 'No'}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Data Source:</strong> {debugInfo.dataSource}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Active Members:</strong> {debugInfo.activeMembers}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-300">
                <strong>Billable Member Count:</strong> {debugInfo.billableMemberCount}
              </div>
              {debugInfo.usageData && (
                <div className="text-xs text-blue-600 dark:text-blue-300">
                  <strong>Credits Used:</strong> {debugInfo.usageData.creditsUsed || 0}
                </div>
              )}
              <details className="mt-2">
                <summary className="text-xs cursor-pointer text-blue-600 dark:text-blue-300 hover:underline">
                  Show Raw Debug Data
                </summary>
                <pre className="account-modal-shape-sm text-xs text-blue-600 dark:text-blue-300 mt-2 bg-blue-100 dark:bg-blue-900/40 p-2 rounded overflow-auto">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </details>
            </div>
          </details>
        )}
        
        {/* Debug info for development - temporary */}
        {process.env.NODE_ENV === 'development' && (
          <details className="account-modal-shape-lg rounded-2xl p-4 shadow-sm border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 mb-4">
            <summary className="text-sm font-semibold mb-2 text-yellow-700 dark:text-yellow-400 cursor-pointer hover:underline">
              Debug: User Role Detection (Click to expand)
            </summary>
            <div className="mt-3 space-y-1 text-xs text-yellow-600 dark:text-yellow-300">
              <div><strong>User Email:</strong> {user?.email || 'Not found'}</div>
              <div><strong>Method:</strong> Fetching from /organization API (userRole property)</div>
              <div><strong>organizationRole:</strong> {user?.organizationRole || 'Not set'}</div>
              <div><strong>company.role:</strong> {user?.company?.role || 'Not set'}</div>
              <div><strong>role:</strong> {user?.role || 'Not set'}</div>
              <div><strong>isCompanyOwner:</strong> {user?.isCompanyOwner?.toString() || 'Not set'}</div>
              <div><strong>isOwner:</strong> {user?.isOwner?.toString() || 'Not set'}</div>
              <div><strong>Detected as Owner:</strong> {isOrgOwner ? 'YES' : 'NO'}</div>
              <div><strong>Usage Controls Visible:</strong> {isOrgAdminOrOwner ? 'Should be visible' : 'Hidden'}</div>
              <div><strong>Credit Model Controls:</strong> {isOrgOwner ? 'Owner can change' : 'Owner only'}</div>
              <div className="account-modal-shape-sm mt-2 p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs">
                <strong>Note:</strong> Owner role is now fetched from organization API (/organization endpoint), 
                not from user object. Check console for "Organization role check" log.
              </div>
              <details className="mt-2">
                <summary className="text-xs cursor-pointer text-yellow-600 dark:text-yellow-300 hover:underline">
                  Show Full User Object
                </summary>
                <pre className="account-modal-shape-sm text-xs text-yellow-600 dark:text-yellow-300 mt-2 bg-yellow-100 dark:bg-yellow-900/40 p-2 rounded overflow-auto max-h-32">
                  {JSON.stringify(user, null, 2)}
                </pre>
              </details>
            </div>
          </details>
        )}
        
        {/* Current Period Charges Section */}
        {billingData?.upcomingInvoice && (
          <div className={sectionCardCls} data-section="current-charges">
            <div className="flex items-center justify-between mb-6">
              <h2 className={sectionTitleLgCls}>Current Period Charges</h2>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Next bill: {formatDate(billingData.upcomingInvoice.next_payment_attempt || billingData.nextBillingDate)}
                </div>
                <ManageBillingButton text="Manage" />
              </div>
            </div>
            
            {/* Regular Charges */}
            {billingData.upcomingInvoice.regularCharges && billingData.upcomingInvoice.regularCharges.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Regular Subscription</h3>
                <div className="space-y-2">
                  {billingData.upcomingInvoice.regularCharges.map((charge, index) => (
                    <div key={charge.id || index} className="flex justify-between items-center py-2">
                      <div>
                        <p className="font-medium">{charge.description}</p>
                        {charge.quantity > 1 && charge.productMetadata?.show_quantity !== 'false' && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {charge.quantity} × {formatCurrencyDollars(charge.unitAmount / 100)}
                          </p>
                        )}
                      </div>
                      <span className="font-medium">
                        {formatCurrencyDollars(charge.amount / 100)}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Conditional separator line if there are prorated charges */}
                {billingData.upcomingInvoice.hasProrations && (
                  <div className="mt-4 border-b border-gray-200 dark:border-[#2a2a2a]"></div>
                )}
              </div>
            )}
            
            {/* Prorated Charges */}
            {billingData.upcomingInvoice.hasProrations && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Prorated Charges</h3>
                  <div className="group relative">
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="account-modal-shape absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 z-10">
                      <div className="text-center">
                        <p className="font-medium mb-1">Prorated Charges</p>
                        <p>When team members are added mid-cycle, you're charged for the remaining days in your billing period. The full seat price then applies starting on your next billing date.</p>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {billingData.upcomingInvoice.currentPeriodCharges.map((charge, index) => (
                    <div key={charge.id || index} className="flex justify-between items-center py-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{formatProratedDescription(charge)}</p>
                          {charge.isProration && (
                            <div className="flex items-center gap-1">
                              <span className="text-xs bg-gray-100 dark:bg-[#282828] text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full">
                                Prorated
                              </span>
                              {(() => {
                                const memberInfo = getMemberInfoForProratedCharge(charge);
                                return memberInfo ? (
                                  <div className="group relative">
                                    <svg className="w-3 h-3 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="account-modal-shape absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-48 z-10">
                                      <div className="text-center">
                                        <p className="font-medium">
                                          {memberInfo.memberName} joined on {formatDate(memberInfo.joinDate)}
                                        </p>
                                        <p className="text-gray-300 dark:text-gray-400 mt-1">
                                          with {memberInfo.daysDiff} day{memberInfo.daysDiff !== 1 ? 's' : ''} remaining in the billing cycle
                                        </p>
                                      </div>
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                        {charge.quantity > 1 && charge.productMetadata?.show_quantity !== 'false' && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {charge.quantity} × {formatCurrencyDollars(charge.unitAmount / 100)} (prorated)
                          </p>
                        )}
                        {charge.period && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Partial period: {formatDate(charge.period.start)} - {formatDate(charge.period.end)}
                          </p>
                        )}
                      </div>
                      <span className="font-medium">
                        {formatCurrencyDollars(charge.amount / 100)}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="account-modal-shape mt-3 p-3 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    <strong>How prorated billing works:</strong> When team members join mid-cycle, you're charged immediately for the remaining days in your current billing period. On your next billing date, the full seat price is included in your regular subscription renewal.
                  </p>
                </div>
              </div>
            )}
            
            {/* Total for Current Period */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-[#2a2a2a] font-semibold">
              <div>
                <p className="font-semibold">Total for Current Period</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                  {formatDate(billingData.upcomingInvoice.period_start)} - {formatDate(billingData.upcomingInvoice.period_end)}
                </p>
              </div>
              <span className="font-semibold text-lg">
                {formatCurrencyDollars(billingData.upcomingInvoice.total / 100)}
              </span>
            </div>
          </div>
        )}

        {/* Invoices Section */}
        <div className={sectionCardCls} data-section="invoices">
          <div className="flex items-center justify-between mb-6">
            <h2 className={sectionTitleLgCls}>Invoices</h2>
          </div>
          
          {invoicesLoading && !invoices.length ? (
            <div className="flex items-center justify-center py-8">
              <Lottie
                animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                loop={true}
                style={{ width: 32, height: 32 }}
              />
              <span className="ml-3 text-gray-500 dark:text-gray-400">Loading invoices...</span>
            </div>
          ) : invoicesError && !invoices.length ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-3">{invoicesError}</p>
              <button
                onClick={fetchInvoices}
                className="account-modal-btn px-4 py-2 text-sm bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200"
              >
                Try Again
              </button>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No invoices found</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Invoices will appear here once you have billing history
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice, index) => (
                <div 
                  key={invoice.id} 
                  className={`flex items-center justify-between py-3 ${
                    index < invoices.length - 1 ? 'border-b border-gray-200 dark:border-[#2a2a2a]' : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-medium">
                        {formatDate(new Date(invoice.created * 1000))}
                      </span>
                      {getInvoiceStatusBadge(invoice.status)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {(() => {
                        const period = getActualBillingPeriod(invoice);
                        return period.start && period.end ? (
                          <span>{formatDate(new Date(period.start * 1000))} - {formatDate(new Date(period.end * 1000))}</span>
                        ) : (
                          <span>Billing period not available</span>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-medium">
                        {formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}
                      </div>
                      {invoice.amount_due > 0 && invoice.status !== 'paid' && (
                        <div className="text-sm text-orange-600 dark:text-orange-400">
                          Due: {formatCurrency(invoice.amount_due, invoice.currency)}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleDownloadInvoice(invoice.id, invoice.number)}
                      disabled={downloadingInvoice === invoice.id}
                      className="account-modal-btn px-3 py-1.5 text-sm border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-[#252525] disabled:opacity-50 flex items-center gap-2"
                    >
                      {downloadingInvoice === invoice.id ? (
                        <>
                          <Lottie
                            animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                            loop={true}
                            style={{ width: 14, height: 14 }}
                          />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className={sectionCardCls}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={sectionTitleCls}>Payment Method</h2>
            <ManageBillingButton text={paymentMethods.length > 0 ? "Update" : "Add"} />
          </div>
          
          {paymentMethods.length > 0 ? (
            <div className="flex items-center space-x-3">
              {getCardBrandIcon(paymentMethods[0]?.brand || paymentMethods[0]?.card?.brand)}
              <div>
                <p className="font-medium">
                  {(paymentMethods[0]?.brand || paymentMethods[0]?.card?.brand || 'Card').charAt(0).toUpperCase() + (paymentMethods[0]?.brand || paymentMethods[0]?.card?.brand || 'Card').slice(1)} •••• {paymentMethods[0]?.last4 || paymentMethods[0]?.card?.last4}
                </p>
                {(paymentMethods[0]?.exp_month || paymentMethods[0]?.card?.exp_month) && (paymentMethods[0]?.exp_year || paymentMethods[0]?.card?.exp_year) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Expires {(paymentMethods[0]?.exp_month || paymentMethods[0]?.card?.exp_month).toString().padStart(2, '0')}/{(paymentMethods[0]?.exp_year || paymentMethods[0]?.card?.exp_year).toString().slice(-2)}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No payment method on file</p>
          )}
        </div>

      </div>
    </>
  );
};

export default BillingSection;
